import { Link } from "@/lib/router-compat";
import { ArrowRight, MapPin, Palette, Sparkles, Sofa, Flower2, Lamp, Frame } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import MagneticButton from "@/components/MagneticButton";
import Testimonials from "@/components/Testimonials";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const services = [
  { icon: Palette, title: "Conseil en couleurs", desc: "Je viens avec les échantillons, on les pose sur vos murs et on regarde ce que ça donne le matin puis en fin de journée. Vous repartez avec les références exactes, marque et code teinte." },
  { icon: Sofa, title: "Sélection mobilier", desc: "Une liste de meubles et de textiles avec les dimensions, les liens et les prix. Vous commandez vous-même, ou je m'en charge." },
  { icon: Lamp, title: "Éclairage & ambiance", desc: "Trois sources par pièce plutôt qu'un plafonnier seul. Températures de couleur, variateurs, emplacements des prises si le chantier le permet." },
  { icon: Frame, title: "Planches d'ambiance", desc: "Une planche par pièce, avec les matières et les teintes côte à côte. C'est souvent ce qui permet de trancher entre deux directions." },
  { icon: Flower2, title: "Art & objets", desc: "Tableaux, céramiques, plantes qui tiennent vraiment dans votre lumière. Le détail qui empêche une pièce de ressembler à un catalogue." },
  { icon: Sparkles, title: "Coaching déco", desc: "Deux heures chez vous, pièce par pièce. À la fin, vous avez une liste de choses à faire, dans l'ordre, avec un budget en face." },
];

const pourquoi = [
  "Un avis extérieur, qui ne vit pas dans la pièce depuis dix ans",
  "Des couleurs, des matières et du mobilier qui tiennent ensemble",
  "Moins d'achats regrettés, donc du budget gardé pour ce qui compte",
  "Des artisans et fournisseurs avec qui je travaille déjà",
  "Un résultat qui vous ressemble, pas une reproduction de Pinterest",
];

interface FeaturedProject {
  id: string; title: string; slug: string; cover_image: string | null; location: string | null;
}

const DecorationInterieureLyon = () => {
  const [projects, setProjects] = useState<FeaturedProject[]>([]);

  useEffect(() => {
    supabase.from("projects").select("id, title, slug, cover_image, location")
      .eq("published", true).order("display_order").limit(4)
      .then(({ data }) => setProjects(data ?? []));
  }, []);

  return (
    <Layout>
      <SEOHead
        title="Décoration intérieure Lyon & Ouest Lyonnais | JIIA Studio"
        description="Décoratrice d'intérieur à Lyon : conseil couleurs, sélection mobilier, planches d'ambiance, coaching déco. Transformez votre intérieur avec JIIA Studio."
        canonical="/decoration-interieure-lyon"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Décoration intérieure Lyon",
            "provider": { "@type": "LocalBusiness", "name": "JIIA Studio", "url": "https://jiia-studio.com" },
            "areaServed": [{ "@type": "City", "name": "Lyon" }, { "@type": "AdministrativeArea", "name": "Rhône-Alpes" }],
            "serviceType": "Décoration intérieure",
            "description": "Conseil en décoration, colorimétrie, sélection mobilier et coaching déco à Lyon et Ouest Lyonnais."
          }
        ]}
      />

      {/* Hero */}
      <section className="py-24 md:py-32 bg-secondary/30">
        <div className="container">
          <AnimatedSection>
            <div className="flex items-center gap-2 text-sm text-muted-foreground font-body mb-4">
              <MapPin className="h-4 w-4" />
              <span>Lyon · Ouest Lyonnais · Rhône-Alpes</span>
            </div>
            <h1 className="font-heading text-4xl md:text-6xl font-light leading-tight mb-6">
              Décoration intérieure<br />
              <span className="italic">à Lyon & Ouest Lyonnais</span>
            </h1>
            <p className="text-lg text-muted-foreground font-body max-w-2xl leading-relaxed mb-8">
              Changer l'ambiance d'un logement sans toucher aux cloisons, c'est souvent une affaire
              de teintes, de lumière et de trois meubles bien choisis. Je travaille aussi bien sur une
              seule pièce que sur un appartement entier, du coaching de deux heures à la décoration complète.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <MagneticButton>
                  <span className="flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-sm font-body text-sm uppercase tracking-widest hover:bg-primary/90 transition-colors">
                    Prendre rendez-vous <ArrowRight className="h-4 w-4" />
                  </span>
                </MagneticButton>
              </Link>
              <Link to="/projets">
                <MagneticButton>
                  <span className="flex items-center gap-2 px-8 py-4 border border-border rounded-sm font-body text-sm uppercase tracking-widest hover:bg-secondary transition-colors">
                    Voir les réalisations
                  </span>
                </MagneticButton>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 md:py-28">
        <div className="container">
          <AnimatedSection>
            <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-3">Services déco</p>
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-12">
              Mes prestations de décoration
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((s, i) => (
              <AnimatedSection key={s.title} delay={i * 0.1}>
                <div className="p-6 border border-border rounded-sm hover:border-accent/50 transition-colors h-full">
                  <s.icon className="h-6 w-6 text-accent mb-3" />
                  <h3 className="font-heading text-lg mb-2">{s.title}</h3>
                  <p className="text-sm text-muted-foreground font-body leading-relaxed">{s.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Pourquoi */}
      <section className="py-20 md:py-28 bg-secondary/30">
        <div className="container max-w-3xl">
          <AnimatedSection>
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-8">
              Pourquoi faire appel à une décoratrice d'intérieur ?
            </h2>
            <p className="text-muted-foreground font-body leading-relaxed mb-8">
              La plupart des gens qui m'appellent ont déjà un tableau Pinterest de deux cents images
              et un salon qu'ils n'arrivent pas à trancher. Le problème n'est presque jamais le goût,
              c'est la mise en cohérence : une teinte qui marche sur une photo prise en Californie
              tombe à plat dans un séjour lyonnais orienté nord. Mon travail consiste à traduire vos
              envies dans votre lumière, vos volumes et votre budget.
            </p>
            <ul className="space-y-4">
              {pourquoi.map((p, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <span className="h-2 w-2 rounded-full bg-accent" />
                  </span>
                  <span className="text-muted-foreground font-body leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </AnimatedSection>
        </div>
      </section>

      {/* Projets */}
      {projects.length > 0 && (
        <section className="py-20 md:py-28">
          <div className="container">
            <AnimatedSection>
              <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-3">Inspirations</p>
              <h2 className="font-heading text-3xl md:text-4xl font-light mb-12">
                Décoration : quelques réalisations
              </h2>
            </AnimatedSection>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {projects.map((project, i) => (
                <AnimatedSection key={project.id} delay={i * 0.1}>
                  <Link to={`/projets/${project.slug}`} className="group block">
                    <div className="aspect-[16/10] overflow-hidden rounded-sm mb-3">
                      <img src={project.cover_image || ""} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                    </div>
                    <h3 className="font-heading text-lg group-hover:text-accent transition-colors">{project.title}</h3>
                    {project.location && <p className="text-xs text-muted-foreground font-body flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" /> {project.location}</p>}
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}

      <Testimonials />

      {/* CTA */}
      <section className="py-20 md:py-28 bg-primary text-primary-foreground">
        <div className="container text-center">
          <AnimatedSection>
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-4">
              On commence par quelle pièce ?
            </h2>
            <p className="text-primary-foreground/70 font-body max-w-lg mx-auto mb-8 leading-relaxed">
              Dites-moi ce qui vous bloque chez vous. Premier rendez-vous offert, réponse sous 48h.
            </p>
            <Link to="/contact">
              <MagneticButton>
                <span className="inline-flex items-center gap-2 px-10 py-4 bg-rose text-white rounded-sm font-body text-sm uppercase tracking-widest hover:bg-rose/90 transition-colors">
                  Demander un devis déco <ArrowRight className="h-4 w-4" />
                </span>
              </MagneticButton>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
};

export default DecorationInterieureLyon;
